import React, { useState } from 'react';
import { useWatch, useController } from 'react-hook-form';
import Styles from '@/assets/js/Styles';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { BannerBlock } from '@/declarations/models/blocks/BannerBlock';
import { BlockPath, useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { useStore } from '@/components/store/Store';
import { CommonMediaList } from '@/editor/PageEditor/EditorBlock/MediaBlock/CommonMediaList';
import Container from '@/components/Container';
import { Accordion, AccordionDetails, AccordionSummary, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { isDamsMediaItem } from '@/utils/typeChecks';
import { isDiMuMediaModel } from '@/declarations/models/ExternalMediaModel';
import CheckboxInput from '@/components/forms/CheckboxInput';
import { width } from '@mui/system';

export const BannerBlockAdditionalImages = () => {
  const { t: tComponents } = useTranslation('components');
  const [expanded, setExpanded] = useState(false);
  const { blockPath } = useCurrentBlock();
  const mediaObject = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.mediaobject`>({
    name: `${blockPath}.mediaobject`,
  });

  const mediaitems = useWatch<BlockSpecificPage<BannerBlock>, `${BlockPath}.mediaitems`>({
    name: `${blockPath}.mediaitems`,
  });

  const { state } = useStore();
  const siteId = state.selectedSite?.id;
  if (!mediaObject) {
    return null;
  }
  if (isDiMuMediaModel(mediaObject)) {
    if (!mediaObject.dimuId) {
      return null;
    }
  } else if (isDamsMediaItem(mediaObject)) {
    if (!mediaObject.unique_id) {
      return null;
    }
  } else if (!mediaObject.id) {
    return null;
  }

  return (
    <Container fullWidth left p={0}>
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          width: '100%',
        }}
        expanded={expanded}
        onChange={(_e, exp) => setExpanded(exp)}>
        <AccordionSummary
          sx={{
            flexDirection: 'row-reverse',
            p: 0,
            mb: 1,
          }}
          expandIcon={
            <MaterialSymbol name='chevron_right' sx={expanded ? { transform: 'rotate(270deg)' } : undefined} />
          }>
          <Typography
            sx={{
              fontWeight: 'bold',
              px: 1,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}>
            {tComponents('BannerBlock.AddSlideshowImagesLabel', {
              count: (mediaitems?.length ?? 0) + (mediaObject ? 1 : 0),
            })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 0,
          }}>
          <Container
            fullWidth
            px={2}
            py={2}
            mt={0}
            gap={2}
            left
            sx={{
              backgroundColor: Styles.Colors.LIGHT_BLUE,
              border: `1px solid ${Styles.Colors.MEDIUM_BLUE}`,
            }}>
            <MaterialSymbol name='eco' color='green' />
            <Typography variant='body2'>{tComponents('BannerBlock.AddSlideshowImagesDescription')}</Typography>
            <div style={{ width: '40%' }}>
              <CheckboxInput
                path={`${blockPath}.randomizeMediaitems`}
                label={tComponents('BannerBlock.AddSlideshowImagesRandomizeLabel')}
              />
            </div>
          </Container>
          <CommonMediaList
            blockPath={`${blockPath}`}
            listName='mediaitems'
            siteId={siteId}
            focusPointSelectable
            sx={{
              p: 1,
              backgroundColor: 'background.default',
            }}
          />
          <Container fullWidth right mt={1}>
            <Button size='large' startIcon={<MaterialSymbol name='close' />} onClick={() => setExpanded(false)}>
              {tComponents('BannerBlock.CloseSlideshowImagesButton')}
            </Button>
          </Container>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};
